export type SocialPlatform =
  | "facebook"
  | "instagram"
  | "tiktok"
  | "twitter"
  | "reddit"
  | "youtube"
  | "linkedin";

export const SOCIAL_PLATFORMS: Array<{ value: SocialPlatform; label: string }> =
  [
    { value: "facebook", label: "Facebook" },
    { value: "instagram", label: "Instagram" },
    { value: "tiktok", label: "TikTok" },
    { value: "twitter", label: "X / Twitter" },
    { value: "reddit", label: "Reddit" },
    { value: "youtube", label: "YouTube" },
    { value: "linkedin", label: "LinkedIn" },
  ];

export interface PlatformGrowthPlan {
  platform: SocialPlatform;
  whyItMatters: string;
  organicStrategy: string[];
  paidStrategy: string[];
  paidTargeting: {
    coreDemographics: string[];
    interests: string[];
  };
  contentPillars: string[];
  cadence: string;
  kpis: string[];
}

export interface AdCopySuggestion {
  headline: string;
  primaryText: string;
  callToAction: string;
  hashtags: string[];
}

export interface VideoPromptVariation {
  title: string;
  detailedPrompt: string;
  recommendedLengthSeconds: {
    geminiVeo: number;
    openAISora: number;
  };
}

export interface CoreTargetAudienceProfile {
  summary: string;
  demographics: string[];
  interests: string[];
  painPoints: string[];
  motivations: string[];
}

export interface BudgetChannelMixItem {
  platform: SocialPlatform;
  splitPercent: number;
  testBudget: string;
  scaleCriteria: string;
}

export interface BudgetChannelMixPlan {
  recommendedMonthlyBudget: string;
  spendSplit: BudgetChannelMixItem[];
}

export interface MarketingActionChecklistItem {
  id: string;
  title: string;
  started: boolean;
}

export interface MarketingAdRecommendation {
  recommendationId: string;
  platform: SocialPlatform;
  assetType: "image" | "video";
  platformTargetUsers: string[];
  angle: string;
  audience: string;
  imagePrompt: string;
  adCopyVariations: AdCopySuggestion[];
  videoPromptVariations?: VideoPromptVariation[];
}

export interface MarketingResearchReport {
  reportId: string;
  userId: string;
  appId: string;
  appName: string;
  appCategory: string;
  model: string;
  summary: string;
  coreTargetAudience: CoreTargetAudienceProfile;
  budgetChannelMixPlan: BudgetChannelMixPlan;
  goToMarketStrategy: string[];
  marketingInsights: string[];
  platformPlans: PlatformGrowthPlan[];
  adRecommendations: MarketingAdRecommendation[];
  actionChecklist: MarketingActionChecklistItem[];
  requestedBudgetRange?: string;
  createdAt: Date;
  savedAt?: Date;
}

export interface CreateMarketingResearchDTO {
  appId: string;
  appName: string;
  appDescription: string;
  appCategory: string;
  appPlatform: "ios" | "android" | "both";
  storeUrl?: string;
  androidStoreUrl?: string;
  iosStoreUrl?: string;
  researchContext?: import("./app").AppResearchContext;
  targetAudience?: string;
  goals?: string[];
  budgetRange?: string;
}

export interface MarketingAsset {
  assetRecordId: string;
  userId: string;
  appId: string;
  appName: string;
  reportId: string;
  recommendationId: string;
  platform: SocialPlatform;
  mediaType: "image" | "video";
  prompt: string;
  headline: string;
  primaryText: string;
  callToAction: string;
  hashtags: string[];
  image?: {
    assetId: string;
    provider: "gemini" | "openai";
    model: string;
    downloadUrl: string;
    storagePath: string;
    mimeType: string;
    sizeBytes: number;
    createdAt: string;
  };
  video?: {
    assetId: string;
    provider: "gemini" | "openai";
    model: string;
    downloadUrl: string;
    storagePath: string;
    mimeType: string;
    sizeBytes: number;
    createdAt: string;
  };
  createdAt: Date;
}

export interface MarketingCreator {
  creatorId: string;
  userId: string;
  appId: string;
  reportId?: string;
  name: string;
  handle?: string;
  platform: string;
  audienceSize?: number;
  niche?: string;
  email?: string;
  ratePaid: number;
  currency: string;
  status: "planned" | "contacted" | "contracted" | "completed" | "cancelled";
  notes?: string;
  createdAt: Date;
  updatedAt?: Date;
}
